import React, { type ReactNode, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	getProductAndIDFromARI,
	isJiraProjectField,
	isSearchResultJiraBoard,
	isSearchResultJiraIssue,
	isSearchResultJiraProject,
	type SearchJiraPartial,
	type SearchResultJiraIssue,
} from '@atlassian/search-client';
import { getJiraUrlWithAnalyticsQueryParams, highlightMatchedText } from '@atlassian/search-common';

import { ApplicationModes } from '../../../../../../common/constants/application-modes';
import { ProductKeys } from '../../../../../../common/constants/products';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { Assignee as AssigneeComponent } from '../../../../../../common/ui/jira-assignee';
import { getStatus } from '../../../../../../common/ui/jira-status';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { MentionsLinkedResults } from '../../../../../../common/ui/linked-results';
import { getJiraEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType as SearchResultTypeText } from '../../../../../../common/ui/search-result-type';
import { BreadcrumbSocialSignal } from '../../../../../../common/ui/social-signals';
import { type AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import {
	enrichExcerpt,
	getJiraSearchResultTypeMetadata,
	processExcerpt,
} from '../../../../../../common/utils/search-result-utils';
import {
	MAX_CHARACTERS_IN_EXCERPT,
	truncateTextWithQuery,
} from '../../../../../../common/utils/truncate-text-with-query';
import { isDummyBoardUrl } from '../../../../../../common/utils/url';
import { useCommonAndPrivateAttributes } from '../../../../../../controllers/analytics';
import { useLogException } from '../../../../../../controllers/error-boundary/utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { useApplicationMode, useBootstrap, useSearchSessionId } from '../../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../../controllers/store/bootstrap/accessors';
import { useIsJiraHydrationLoading, useSearchQuery, useSelectedProduct } from '../../../../../../controllers/store/search';

import { ProjectMetadata } from './project-metadata';

const findProject = (partial: SearchResultJiraIssue) => {
	if (!partial.issue) {
		return;
	}

	for (const field of partial.issue.fieldsById.edges) {
		if (isJiraProjectField(field.node)) {
			return field.node;
		}
	}
};

const getIssueOrProjectKey = (partial: SearchJiraPartial) => {
	if (isSearchResultJiraIssue(partial)) {
		return partial.issue?.key;
	}
	if (isSearchResultJiraProject(partial)) {
		return partial.projectKey;
	}
	return null;
};

const addJiraQueryParams = (
	url: string,
	partial: SearchJiraPartial,
	searchSessionId: string,
	projectId?: string,
) => {
	return getJiraUrlWithAnalyticsQueryParams({
		url,
		searchSessionId,
		resultId: partial.id,
		projectId,
	});
};

interface JiraSearchResultProps {
	partial: SearchJiraPartial;
	/** The position in the results list shown to the user. */
	position?: number;
}

type Content = {
	type: string;
	content?: Content[];
	text?: string;
};

const getContentString = (adf: Content, level: number = 0) => {
	let aggregatedString = '';

	if (adf.content) {
		adf.content.forEach((node) => {
			if (node.type === 'text') {
				aggregatedString += node.text;
			} else {
				if (node.content && level < 15) {
					aggregatedString += ' ' + getContentString(node, ++level);
				}
			}
		});
	}
	return aggregatedString;
};

const getDescription = (description: string, query: string) => {
	/**
	 * This description can be either a complicated JSON which is actually an ADF (Atlassian Document Format) schema of the entire content of the jira issue.
	 * or it could be a simple excerpt. This uncertainity is due to the progressive development of the BE feature and rolling it out.
	 */

	try {
		const adf = JSON.parse(description);
		const text = getContentString(adf);

		return truncateTextWithQuery(text, query);
	} catch {
		return description.length > MAX_CHARACTERS_IN_EXCERPT
			? description.substring(0, MAX_CHARACTERS_IN_EXCERPT) + '...'
			: description.substring(0, MAX_CHARACTERS_IN_EXCERPT);
	}
};

export const JiraSearchResult = ({
	partial,
	position,
}: JiraSearchResultProps): React.JSX.Element => {
	const [searchSessionId] = useSearchSessionId();
	const [selectedProduct] = useSelectedProduct();
	const [searchQuery] = useSearchQuery();
	const [primaryProduct] = usePrimaryProduct();
	const [{ cloudId, thirdPartyConfigs }] = useBootstrap();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const isHydrationLoading = useIsJiraHydrationLoading();
	const { description } = partial;
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const applicationMode = useApplicationMode();
	const logException = useLogException();

	let project = '';
	let projectId: string | undefined;
	let Status: React.ReactNode = null;
	let Assignee: React.ReactNode = null;
	let SocialSignal: React.ReactNode = null;

	if (isSearchResultJiraIssue(partial)) {
		const projectField = findProject(partial);
		project = projectField?.project.name || '';
		projectId = projectField?.project.id;

		Status = getStatus(partial.status?.name, partial.status?.colorName, isHydrationLoading);
		Assignee = fg('rovo_search_assignee_field_fps') ? (
			<AssigneeComponent user={partial.issue?.assigneeField?.user} />
		) : null;
	}

	const url = useMemo(() => {
		let resultUrl;
		if (isSearchResultJiraBoard(partial)) {
			const notDummyUrl = isDummyBoardUrl(partial.url) ? '' : partial.url;
			resultUrl = partial.board?.boardFrontendUrl || notDummyUrl;
		} else {
			resultUrl = partial.url;
		}

		return resultUrl ? addJiraQueryParams(resultUrl, partial, searchSessionId, projectId) : '';
	}, [partial, searchSessionId, projectId]);

	if (
		applicationMode === ApplicationModes.Rovo &&
		// social signals are only displayed for jira projects
		isSearchResultJiraProject(partial) &&
		partial.socialSignal &&
		fg('search-plex-jira-social-signal')
	) {
		SocialSignal = <BreadcrumbSocialSignal partial={partial} commonAttributes={commonAttributes} />;
	}

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(partial.id);
		return {
			id: partial.id,
			containerId: null,
			spaceId: null,
			contentType: partial.type,
			entityType: partial.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [partial.id, partial.type]);
	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const enrichedTitle = useMemo(() => {
		const prefix = getIssueOrProjectKey(partial);

		if (fg('enable_jira_result_highlighting')) {
			if (prefix && !partial.title.startsWith(prefix)) {
				return enrichExcerpt(prefix.concat(': ', partial.title));
			}
			return enrichExcerpt(partial.title);
		}

		const { highlightedTitle } = highlightMatchedText(partial.title, searchQuery.split(' '));

		if (prefix && !(highlightedTitle?.startsWith(prefix) || partial.title.startsWith(prefix))) {
			return !highlightedTitle
				? prefix.concat(': ', partial.title)
				: enrichExcerpt(prefix.concat(': ', highlightedTitle));
		}

		return !highlightedTitle ? partial.title : enrichExcerpt(highlightedTitle);
	}, [partial, searchQuery]);

	const enrichedDescription = useMemo(() => {
		if (fg('enable_jira_result_highlighting')) {
			return processExcerpt(description);
		}

		const reducedDescription = getDescription(description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [description, searchQuery]);

	const getLastModified = useMemo(() => {
		if (!(isSearchResultJiraIssue(partial) || isSearchResultJiraProject(partial))) {
			return null;
		}
		if (partial.lastModifiedDate) {
			return <LastModified lastModified={partial.lastModifiedDate} />;
		}
		return null;
	}, [partial]);

	// TODO: Remove this check once backend is fixed to not return boards with dummy URLs
	if (!url && isSearchResultJiraBoard(partial)) {
		// If it is a Jira board with no URL, we don't render it
		logException(
			new Error(String(`Jira Board search result has no URL. Result ID: ${partial.id}`)),
		);
		return <></>;
	}

	const metadataParts: [string, ReactNode][] = [
		['type', <SearchResultTypeText type={getJiraSearchResultTypeMetadata(partial.type)} />],
		project ? ['project', <ProjectMetadata project={project} />] : null,
		['lastModified', getLastModified],
		['assignee', Assignee],
		['social-signal', SocialSignal],
	].filter(Boolean) as [string, ReactNode][];

	const titleMetadataParts: [string, ReactNode][] = [['status', Status]];

	const isIssue = isSearchResultJiraIssue(partial);

	const issueIconUrl = isIssue && partial.issue?.issueTypeAvatarUrl;
	const boardIconUrl = isSearchResultJiraBoard(partial) && partial.board?.iconUrl;
	const iconUrl = issueIconUrl || boardIconUrl || partial.iconUrl;

	const icon = getJiraEntityIcon({ iconUrl, type: partial.type, isIssue, size: 'medium' });

	return (
		<SearchResult
			{...partial}
			product={ProductKeys.Jira}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			title={enrichedTitle}
			headerMetadataParts={metadataParts}
			titleMetadata={<SearchMetadata parts={titleMetadataParts} />}
			description={<DescriptionBody description={enrichedDescription} />}
			cloudId={cloudId}
			position={position}
			partial={partial}
			appMode={applicationMode}
			footer={
				<MentionsLinkedResults
					linkedResults={partial.linkedResults || []}
					thirdPartyConfigs={thirdPartyConfigs}
					partial={partial}
					url={url}
					position={position}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
				/>
			}
		/>
	);
};
